
import { faPresentationScreen } from "@fortawesome/pro-solid-svg-icons/faPresentationScreen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, Grid, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { MprcClickable } from "components";
import { IconAI } from "components/icons";
import { IconPublicationThread } from "components/icons/IconPublicationThread";
import { MprcSwitchPlayback } from "components/MprcSwitchPlayback";
import { PublicationType, SavePublicationResultDto } from "generated/CoreApi_v1";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TranscriptionAiSearchDialog } from "views/knowledge/components/card/actions/TranscriptionAiSearchDialog";
import { BuildLigthBoxSlides, CardLightBox } from "views/knowledge/components/card/behaviors/common/CardLightbox";
import { FavoriteButton, LikeButton, ShareButton } from "views/knowledge/components/card/behaviors/common/Interaction";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { publish } from "views/knowledge/feed/FeedEvents";
import { publicationFactory } from "views/knowledge/feed/FeedHelpers";
import { ModeratorMessageButton } from "../../actions/ModeratorMessageDialog";
import { ThreadPublicationsreOrderDialog } from "../../actions/ThreadPublicationsreOrderDialog";
import { DefaultOptions } from "../common/DefaultOptions";

///
const AddThreadItem = () => {
  const { pub, pub: { interaction: { allowReorder, allowAddThread } } } = useCardContext();
  const [anchor, setAnchor] = React.useState<Element | null>(null);
  const [dialog, setDialog] = React.useState<any>(null);
  const { t } = useTranslation("knowledge");

  const onClose = (result: SavePublicationResultDto | null) => {
    setDialog(null);
    if (result != null && result.success && result.newPublicationId) {
      publish("TreadItemCreated", result.newPublicationId);
    }
  };

  const onReorderClose = (changed: boolean) => {
    setDialog(null);
    if (changed) {
      publish("TreadReload", "");
    }
  };

  const onMenuClick = (option: number) => {
    let pubOptions = { parent: { parentId: pub.publicationId, parentTitle: pub.title } };
    switch (option) {
      case 1:
        setDialog(publicationFactory(null, PublicationType.Post, onClose, pubOptions));
        break;
      case 2:
        setDialog(publicationFactory(null, PublicationType.Poll, onClose, pubOptions));
        break;
      case 3:
        setDialog(<ThreadPublicationsreOrderDialog
          masterPublicationId={pub.publicationId}
          title={pub.title}
          onClose={onReorderClose}
        />);
        break;
    }
    setAnchor(null);
  };

  if (!allowAddThread) return <></>;

  return (
    <>
      {dialog}
      <Typography variant="iconText" component="div">
        <IconButton color="inherit" title={t("Feed.Card.Options.Thread.Tooltip")} onClick={(e) => setAnchor(e.currentTarget)} disabled={pub.readOnly}>
          <IconPublicationThread fixedWidth />
        </IconButton>
        <Menu anchorEl={anchor} open={anchor != null} onClose={() => setAnchor(null)}>
          <MenuItem onClick={() => onMenuClick(1)}>{t("Feed.Card.Options.Thread.AddPostAction")}</MenuItem>
          <MenuItem onClick={() => onMenuClick(2)}>{t("Feed.Card.Options.Thread.AddPollAction")}</MenuItem>
          {allowReorder &&
            <>
              < Divider />
              <MenuItem onClick={() => onMenuClick(3)}>{t("Feed.Card.Options.Thread.ReorderAction")}</MenuItem>
            </>
          }
        </Menu>
      </Typography>
    </>
  );
};

///
export const FeedContentPageFooter = () => {
  const { t } = useTranslation("knowledge");
  const { pub: { publicationId, title, presentation, attachs, blocked, interaction }, helper, cardOwnProps, onUpdateCardOwnProps } = useCardContext();
  const [openSearch, setOpenSearch] = useState<boolean>(false);

  const [openLightbox, setOpenLightbox] = useState<boolean>(false);

  const slides = useMemo(() => (
    presentation && attachs
      ? BuildLigthBoxSlides(presentation, attachs, presentation.rotationInterval == 0)
      : []
  ), [publicationId]);

  const att = attachs?.find(p => p.attachId == cardOwnProps.presentation.currentAttachId);
  const showIADialog = interaction.allowIaExplains && (att?.hasTranscription === true || att?.hasSearchData === true);

  return (
    <>
      {openSearch &&
        <TranscriptionAiSearchDialog
          attachId={cardOwnProps.presentation!.currentAttachId!}
          title={title}
          onClose={() => setOpenSearch(false)}
        />
      }

      <Grid container justifyContent="space-between" alignItems="center">
        <Grid>
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid>
              <LikeButton />
            </Grid>
          </Grid>
        </Grid>

        {
          blocked?.blockedOn != null &&
          <Grid>
            <ModeratorMessageButton optionMode="blocked" />
          </Grid>
        }

        {
          (helper.hasAutoPlayPresentation || helper.hasPresentation || showIADialog) &&
          <Grid>

            {showIADialog &&
              <MprcClickable onClick={() => setOpenSearch(true)}>
                <IconAI fixedWidth /> {t("Feed.AITranscriptionSearch")}
              </MprcClickable>
            }

            <Grid container spacing={1} justifyContent="center" alignItems="center">
              {
                helper.hasAutoPlayPresentation &&
                <Grid>
                  <MprcSwitchPlayback
                    title={t("Feed.AutomaticRotation")}
                    checked={cardOwnProps.presentation.autoplay}
                    onChange={
                      (_, checked) => onUpdateCardOwnProps({ presentation: { ...cardOwnProps.presentation, autoplay: checked } })
                    }
                  />
                </Grid>
              }

              {
                helper.hasPresentation && presentation &&
                <Grid>
                  <Typography variant="iconText">
                    <IconButton
                      color="inherit"
                      title={t("Feed.StartPresentation")}
                      onClick={() => {
                        setOpenLightbox(true);
                        onUpdateCardOwnProps({ presentation: { ...cardOwnProps.presentation, lighboxOpened: true } });
                      }}
                    >
                      <FontAwesomeIcon icon={faPresentationScreen} color="inherit" />
                    </IconButton>
                  </Typography>
                  <CardLightBox
                    slides={slides}
                    open={openLightbox}
                    index={0}
                    autoplay={presentation!.rotationInterval > 0}
                    rotationInterval={presentation!.rotationInterval > 0 ? presentation!.rotationInterval : 5}
                    onClose={() => {
                      setOpenLightbox(false);
                      onUpdateCardOwnProps({
                        presentation: { ...cardOwnProps.presentation, lighboxOpened: false }
                      });
                    }}
                  />
                </Grid>
              }
            </Grid>

          </Grid>
        }
        <Grid>
          <Grid container justifyContent="center" alignItems="center">
            <Grid>
              <FavoriteButton />
            </Grid>
            <Grid>
              <ShareButton />
            </Grid>
            <Grid>
              <AddThreadItem />
            </Grid>
            <Grid size="grow">
              <DefaultOptions />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
